<!--
  ScanModelSource — "Scan 3D" inside a surface that attaches a model (card
  `t_92f7411f`, E3).

  The owner, 2026-10-11: "can you place the scan app inside dashboard edit
  modal.. scan with 3d... then when complete upload to hub directly" + the scans
  must show up as the dashboard's 3D model.

  Three things, and no fourth:

   1. a **Scan 3D** button — the existing capture flow (`RoomScanPanel`, iOS +
      LiDAR gates exactly as the rooms entry and the 3D Scans page have them). A
      non-iOS shell is told in words that the capture runs in the Torik app on an
      iPhone Pro; there is no button that cannot work;
   2. a finished capture is **uploaded to this hub on its own** (E2's
      `useScanUpload` — the same transfer the settings page runs), and the fresh
      scan is then **offered as the model source immediately** (it becomes the
      selection and the list shows it);
   3. the hub's existing scans are listed, each with the file a model would be
      built from (`model.glb` first, else the capture's own `.usdz`), so any of
      them can be chosen as the source. A package with no geometry is shown but
      cannot be chosen — the model would have nothing to render.

  It does NOT attach anything itself: choosing a row reports the scan upward, and
  the host (the dashboard editor on Save, the upload modal on Upload) runs
  `services/scanModelAttach.ts`. That is what keeps "select → attach → save" one
  act for the person, and keeps a Cancel from having written anything.

  The strings live in the six catalogues under `views.scans.model.*`, because
  this panel renders inside a hub tab where the reader's language may be any of
  the six (skill rule 10).
-->
<template>
  <div class="space-y-3" data-scan-source>
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div class="min-w-0">
        <p class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ t('views.scans.model.title') }}</p>
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ t('views.scans.model.intro') }}</p>
      </div>
      <div class="flex flex-shrink-0 flex-wrap items-center gap-2">
        <button
          v-if="isIos"
          type="button"
          data-scan-source-scan
          class="flex items-center gap-2 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="busy || disabled"
          @click="scanPanelOpen = true">
          <span class="material-symbols-outlined text-lg">view_in_ar</span>
          {{ t('views.scans.model.scan3d') }}
        </button>
        <button
          type="button"
          data-scan-source-refresh
          class="rounded-lg bg-gray-200 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
          :disabled="loading || busy || disabled"
          @click="refresh">
          {{ t('views.scans.refresh') }}
        </button>
      </div>
    </div>

    <p v-if="!isIos" class="text-xs text-gray-500 dark:text-gray-400" data-scan-source-no-capture>
      {{ t('views.roomScan.unsupportedPlatform') }}
    </p>

    <!-- The one transfer this panel runs: the capture on its way to the hub. -->
    <div
      v-if="busy || failure"
      class="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40"
      data-scan-source-transfer>
      <div v-if="busy" class="space-y-2">
        <p class="text-xs font-medium text-gray-900 dark:text-white">{{ phaseLabel }}</p>
        <div class="h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
          <div
            class="h-1.5 rounded-full bg-primary-600 transition-all duration-300"
            :class="{ 'animate-pulse': progress === null }"
            :style="{ width: progress === null ? '100%' : `${progress}%` }"></div>
        </div>
        <p class="text-xs text-gray-500 dark:text-gray-400">
          {{
            progress === null
              ? t('views.scans.progressUnknown')
              : t('views.scans.progress', { percent: Math.round(progress) })
          }}
        </p>
      </div>
      <div v-else data-scan-source-error>
        <p class="text-xs text-red-700 dark:text-red-300">{{ failureSentence }}</p>
        <button
          type="button"
          class="mt-2 rounded-lg bg-gray-200 px-3 py-1 text-xs text-gray-700 transition-colors hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
          @click="retry">
          {{ t('views.scans.retry') }}
        </button>
      </div>
    </div>

    <!-- The capture is on the hub. -->
    <p
      v-if="notice"
      class="rounded-lg border border-green-200 bg-green-50 p-2 text-xs text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-200"
      data-scan-source-notice>
      {{ notice }}
    </p>

    <!-- The scans on this hub, each one a candidate model source. -->
    <div class="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
      <p v-if="loading" class="p-3 text-xs text-gray-500 dark:text-gray-400">{{ t('views.scans.loading') }}</p>
      <p v-else-if="loadError" class="p-3 text-xs text-red-700 dark:text-red-300" data-scan-source-load-error>
        {{ loadError }}
      </p>
      <p v-else-if="scans.length === 0" class="p-3 text-xs text-gray-500 dark:text-gray-400" data-scan-source-empty>
        {{ t('views.scans.model.empty') }}
      </p>
      <template v-else>
        <div
          v-for="scan in scans"
          :key="scan.id"
          class="flex items-start gap-3 border-b border-gray-200 p-3 last:border-b-0 dark:border-gray-700"
          :class="scan.id === selectedScanId ? 'bg-primary-50 dark:bg-primary-900/20' : ''"
          :data-scan-id="scan.id"
          data-scan-source-row>
          <span class="material-symbols-outlined mt-0.5 text-xl text-gray-400 dark:text-gray-500">view_in_ar</span>
          <div class="min-w-0 flex-1">
            <p class="truncate text-sm font-medium text-gray-900 dark:text-white">
              {{ scan.name || scan.label || scan.id }}
            </p>
            <div class="mt-1 flex flex-wrap items-center gap-2">
              <span
                v-if="scan.mode"
                class="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                {{ scan.mode === 'structure' ? t('views.scans.modeStructure') : t('views.scans.modeRoom') }}
              </span>
              <span
                v-if="typeof scan.roomCount === 'number' && scan.roomCount > 0"
                class="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                {{ t('views.scans.rooms', { count: scan.roomCount }) }}
              </span>
              <span
                v-if="modelNameOf(scan)"
                class="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                data-scan-source-model-file>
                {{ modelNameOf(scan) }}
              </span>
              <span v-else class="text-xs text-amber-700 dark:text-amber-300" data-scan-source-no-model>
                {{ t('views.scans.model.noModel') }}
              </span>
              <span class="text-xs text-gray-500 dark:text-gray-400">
                {{ t('views.scans.files', { count: scan.fileCount }) }} ·
                {{ formatScanDate(scan.capturedAt || scan.createdAt) }}
              </span>
            </div>
          </div>
          <div class="flex flex-shrink-0 items-center gap-2">
            <span
              v-if="scan.id === selectedScanId"
              class="rounded-full bg-primary-100 px-2 py-0.5 text-xs text-primary-700 dark:bg-primary-900/40 dark:text-primary-200"
              data-scan-source-selected>
              {{ t('views.scans.model.selected') }}
            </span>
            <button
              v-else
              type="button"
              data-scan-source-use
              class="rounded-lg border border-gray-300 px-3 py-1 text-xs font-medium text-gray-800 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-700"
              :disabled="busy || disabled || !modelNameOf(scan)"
              @click="choose(scan)">
              {{ t('views.scans.model.useScan') }}
            </button>
          </div>
        </div>
      </template>
    </div>

    <!-- The capture flow itself: unchanged, and shared with the other entries. -->
    <RoomScanPanel :open="scanPanelOpen" :platform="platform" @close="scanPanelOpen = false" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Capacitor } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';
import RoomScanPanel from './RoomScanPanel.vue';
import { listHubScans, type HubScan } from '../../services/scanApi';
import { scanModelFileName } from '../../services/scanModelAttach';
import {
  scanTransferError,
  useScanUpload,
  type ScanTransferError,
} from '../../composables/useScanUpload';
import { TRANSFER_ERROR_SENTENCES } from '../../utils/scanTransferText';
import { onCaptureFinished, type RoomScanCapture } from '../../plugins/roomScan';
import { formatDateTime } from '../../utils/formatDate';

defineProps<{
  /** The scan chosen as this surface's model right now (its row is marked). */
  selectedScanId?: string | null;
  /** A transfer elsewhere in the host is running: hold every action, hide nothing. */
  disabled?: boolean;
}>();

const emit = defineEmits<{ 'update:selectedScan': [HubScan | null] }>();

const { t } = useI18n();

/** The shell this panel runs in: `ios` in the app, `web`/`android` elsewhere. */
const platform = Capacitor.getPlatform();
const isIos = computed(() => platform === 'ios');

const scans = ref<HubScan[]>([]);
const loading = ref(false);
const loadError = ref('');
const notice = ref('');
const failure = ref<ScanTransferError | null>(null);
const scanPanelOpen = ref(false);
/** The last refused action, so "Try again" re-runs it. */
const lastAttempt = ref<(() => Promise<void>) | null>(null);

const {
  phase,
  progress,
  error: transferError,
  busy,
  uploadCapture,
} = useScanUpload();

const phaseLabel = computed(() => {
  switch (phase.value) {
    case 'reading':
      return t('views.scans.reading');
    case 'uploading':
      return t('views.scans.uploading');
    default:
      return t('views.scans.working');
  }
});

const failureSentence = computed(() => {
  const reported = failure.value;
  if (!reported) return '';
  const [key, fallback] = TRANSFER_ERROR_SENTENCES[reported.code];
  return `${t(key, fallback)} — ${reported.detail}`;
});

/** The file a dashboard's model would be built from, if the package carries one. */
function modelNameOf(scan: HubScan): string | null {
  return scanModelFileName(scan);
}

function formatScanDate(value: string): string {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : formatDateTime(parsed);
}

async function refresh(): Promise<void> {
  loading.value = true;
  loadError.value = '';
  try {
    scans.value = await listHubScans();
  } catch (error) {
    const reason = error instanceof Error ? error.message : String(error);
    loadError.value = `${t('views.scans.loadFailed')} — ${reason}`;
    scans.value = [];
  } finally {
    loading.value = false;
  }
}

/**
 * Run one action, then re-read the hub list — the panel never guesses the
 * result. Failures land in one place: what the action threw, or what the
 * composable stored while it ran (its verbs answer `null` instead of throwing).
 */
async function attempt(work: () => Promise<void>): Promise<void> {
  lastAttempt.value = work;
  failure.value = null;
  notice.value = '';
  try {
    await work();
  } catch (error) {
    failure.value = scanTransferError(error);
  }
  if (!failure.value && transferError.value) failure.value = transferError.value;
  await refresh();
}

async function retry(): Promise<void> {
  const work = lastAttempt.value;
  if (!work) {
    failure.value = null;
    return;
  }
  await attempt(work);
}

function choose(scan: HubScan): void {
  notice.value = '';
  emit('update:selectedScan', scan);
}

// ── The capture flow ─────────────────────────────────────────────────────────
// The native RoomPlan UI is a full-screen view controller over the WebView: a
// finished capture arrives as an event, and the upload starts on its own (the
// owner's ask: "when complete upload to hub directly"). The fresh scan is
// offered as the model source in the same breath.
const listeners: PluginListenerHandle[] = [];

onMounted(async () => {
  await refresh();
  if (!isIos.value) return;
  listeners.push(
    await onCaptureFinished(async (capture: RoomScanCapture) => {
      await attempt(async () => {
        const scan = await uploadCapture(capture);
        if (scan) {
          notice.value = t('views.scans.uploadDone', { name: scan.name || capture.label || capture.id });
          emit('update:selectedScan', scan);
        }
      });
    }),
  );
});

onUnmounted(() => {
  for (const listener of listeners) void listener.remove();
  listeners.length = 0;
});

defineExpose({ refresh });
</script>
